import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#onStoreLocatorClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#onStoreLocatorClick);
  }

  /**
   * Restarts the store-locator icon's continuous bounce (see
   * expand-resources/base/scss/_header-actions.scss) from the beginning when
   * tapped, instead of leaving it to finish its current one-second cycle.
   * @param {MouseEvent} event
   */
  #onStoreLocatorClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const icon = target.closest('.action__store-locator')?.querySelector('.svg-wrapper');
    if (!icon) return;

    icon.style.animation = 'none';
    // Force reflow so removing the override below restarts the animation.
    void icon.offsetWidth;
    icon.style.animation = '';
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
